import { useEffect, useState } from "react";
import client from "@/api/client";
import { PageHeader, Section, StatusPill } from "@/components/PageChrome";
import { METRIC_COLORS } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import {
  Package, Barcode, Archive, Truck, ClipboardText,
  Circle, ArrowRight, CheckCircle, Megaphone,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const METRIC_LINKS = {
  todays_orders:          "/orders",
  pending_picking:        "/picking",
  pending_scanning:       "/scanning",
  pending_counting:       "/scanning",
  pending_packing:        "/packing",
  pending_outward:        "/dispatch",
  ready_dispatch:         "/dispatch",
  todays_dispatched:      "/pipeline-report/dispatched",
  no_stock_current_month: "/pipeline-report/no_stock",
  hold_orders:            "/pipeline-report/hold",
  pending_grsr:           "/grsr",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [notes, setNotes] = useState([]);

  const load = async () => {
    const [{ data: s }, { data: r }, { data: n }] = await Promise.all([
      client.get("/dashboard/stats"),
      client.get("/dashboard/recent"),
      client.get("/dashboard/notes"),
    ]);
    setStats(s); setRecent(r); setNotes(n);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const cards = stats ? Object.entries(METRIC_COLORS).map(([k, meta]) => {
    let extra = null;
    if (k === "todays_orders" && stats.todays_order_quantity != null) {
      extra = `Qty ${stats.todays_order_quantity}`;
    }
    if (k === "pending_scanning" && stats.pending_scanning_bill_value) {
      extra = `Bill Value ${inr(stats.pending_scanning_bill_value)}`;
    }
    if (k === "ready_dispatch" && stats.ready_dispatch_bill_value) {
      extra = `Bill Value ${inr(stats.ready_dispatch_bill_value)}`;
    }
    if (k === "no_stock_current_month") {
      extra = "picking · approx qty 0";
    }
    if (k === "hold_orders") {
      extra = "orders with a hold-by";
    }
    if (k === "pending_grsr") {
      const qty = Number(stats.pending_grsr_qty || 0);
      extra = qty > 0 ? `Actual Qty ${qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "no Shopper CN yet";
    }
    if (k === "todays_dispatched") {
      const parts = [];
      if (stats.todays_dispatched_boxes)  parts.push(`${stats.todays_dispatched_boxes} boxes`);
      if (stats.todays_dispatched_pieces) parts.push(`${stats.todays_dispatched_pieces} pcs`);
      if (stats.todays_dispatched_value)  parts.push(inr(stats.todays_dispatched_value));
      if (parts.length) extra = parts.join(" · ");
    }
    return { key: k, ...meta, value: stats[k] ?? 0, to: METRIC_LINKS[k], extra };
  }) : [];

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user.name}`}
        subtitle="Live warehouse status"
      />

      <Section>
        {notes.length > 0 && (
          <div className="mb-6 space-y-2" data-testid="dashboard-notes">
            {notes.map(n => (
              <div key={n.id} data-testid={`dashboard-note-${n.id}`}
                   className="flex items-start gap-3 bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500 rounded-sm p-4">
                <div className="h-8 w-8 rounded-sm bg-amber-500 text-white grid place-items-center shrink-0">
                  <Megaphone size={16} weight="bold"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="label-cap text-amber-800">
                    Admin note · {n.scope === "all" ? "For everyone" : n.scope === "role" ? `For ${n.target}` : "For you"}
                  </div>
                  <div className="text-sm text-amber-950 mt-1 whitespace-pre-line">{n.text}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-metrics">
          {cards.map((c) => (
            <Link
              to={c.to}
              key={c.key}
              data-testid={`metric-${c.key}`}
              className={`group block ${c.bg} border ${c.accent} border-l-4 rounded-sm p-4 hover:shadow-md transition-all hover:-translate-y-0.5`}
            >
              <div className="flex items-start justify-between">
                <div className="label-cap">{c.name}</div>
                <span className={`inline-flex h-2.5 w-2.5 rounded-full ${c.chip}`} />
              </div>
              <div className={`font-display text-5xl font-black mt-3 ${c.text}`}>
                {c.value}
              </div>
              {c.extra && (
                <div className={`mt-2 text-xs font-semibold ${c.text} opacity-80`} data-testid={`metric-${c.key}-extra`}>
                  {c.extra}
                </div>
              )}
              <div className="mt-3 flex items-center gap-1 text-xs text-slate-500 group-hover:text-slate-800">
                View <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white border border-slate-200 rounded-sm">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="label-cap">Recent</div>
                <h3 className="font-display text-lg font-bold">Latest 10 orders</h3>
              </div>
              <Link to="/orders" className="text-xs font-semibold text-slate-700 hover:underline">View all →</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2">Order No</th>
                    <th className="px-4 py-2">Retailer</th>
                    <th className="px-4 py-2">Salesman</th>
                    <th className="px-4 py-2">Qty</th>
                    <th className="px-4 py-2">Stage</th>
                  </tr>
                </thead>
                <tbody data-testid="recent-orders-tbody">
                  {recent.map((o) => (
                    <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono">{o.order_no}</td>
                      <td className="px-4 py-2">{o.retailer_name}</td>
                      <td className="px-4 py-2">{o.salesman}</td>
                      <td className="px-4 py-2">{o.order_quantity}</td>
                      <td className="px-4 py-2"><StatusPill status={o.stage} /></td>
                    </tr>
                  ))}
                  {recent.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No orders yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="label-cap mb-4">Process Flow</div>
            {[
              { i: ClipboardText, l: "Order Received",     c: "text-emerald-600" },
              { i: Package,       l: "Material Picking",   c: "text-amber-600" },
              { i: Barcode,       l: "Scanning & Counting",c: "text-blue-600" },
              { i: Archive,       l: "Packing",            c: "text-violet-600" },
              { i: Truck,         l: "Dispatch",           c: "text-rose-600" },
              { i: CheckCircle,   l: "Completed",          c: "text-slate-800" },
            ].map(({ i: I, l, c }, idx, arr) => (
              <div key={l} className="flex items-center gap-3 py-2">
                <I size={22} weight="duotone" className={c} />
                <div className="flex-1 text-sm font-medium text-slate-800">{l}</div>
                {idx < arr.length - 1 && <Circle size={6} weight="fill" className="text-slate-300" />}
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
